/**
 * `/consent/agent-entered` — where a customer gives their agent permission to enter their
 * information. Presentational; every decision lives in `useAgentEnteredConsent`.
 *
 * Must stay on a PUBLIC route: the customer arrives signed out and may have no account at all.
 */

import { Button, Card, CardContent, Spinner } from '@design-system';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import {
  AgentEnteredConsentResult,
  AgentEnteredConsentStep,
  AgentEnteredVerifyStep,
  useAgentEnteredConsent,
} from '@/features/agent-entered-consent';

export function AgentEnteredConsentPage() {
  const {
    state,
    values,
    setValue,
    canSubmit,
    confirm,
    errorMessage,
    advisorFirstName,
    advisorLastName,
    organizationDisplayName,
    clientFirstName,
    permissionText,
    acknowledgementText,
  } = useAgentEnteredConsent();

  const advisorFullName = [advisorFirstName, advisorLastName].filter(Boolean).join(' ');
  const isTerminal =
    state === 'consented' || state === 'invalidLink' || state === 'alreadyConsented';
  const isBusy = state === 'consenting';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-20 md:pt-24">
        <div className="w-full max-w-lg">
          <Card className="bg-background gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
            <div className="p-1">
              <div className="bg-muted rounded-xl px-6 py-10">
                {state === 'loading' ? (
                  <div className="text-center">
                    <div className="mb-6 flex justify-center">
                      <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
                        <Spinner className="text-primary size-8" />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      Just a moment…
                    </h1>
                  </div>
                ) : isTerminal ? (
                  <AgentEnteredConsentResult
                    variant={state}
                    advisorFirstName={advisorFirstName}
                  />
                ) : (
                  <div className="text-center">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                      {clientFirstName ? `Hi ${clientFirstName},` : 'Permission needed'}
                    </h1>
                    <p className="text-muted-foreground mx-auto mt-3 max-w-md text-base leading-relaxed">
                      {advisorFullName || 'Your agent'}
                      {organizationDisplayName ? ` of ${organizationDisplayName}` : ''} would like
                      to complete your long-term care planning questionnaire on your behalf.
                      Confirm your details below, then review and accept the two statements.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {!isTerminal && state !== 'loading' && (
              <CardContent className="space-y-6 p-6">
                <AgentEnteredVerifyStep
                  email={values.email}
                  birthDate={values.birthDate}
                  disabled={isBusy}
                  onEmailChange={value => setValue('email', value)}
                  onBirthDateChange={value => setValue('birthDate', value)}
                />

                <AgentEnteredConsentStep
                  permissionText={permissionText}
                  acknowledgementText={acknowledgementText}
                  acceptedPermission={values.acceptedPermission}
                  acceptedAcknowledgement={values.acceptedAcknowledgement}
                  disabled={isBusy}
                  onAcceptPermission={accepted => setValue('acceptedPermission', accepted)}
                  onAcceptAcknowledgement={accepted =>
                    setValue('acceptedAcknowledgement', accepted)
                  }
                />

                {errorMessage && (
                  <p
                    role="alert"
                    className="text-destructive text-sm"
                  >
                    {errorMessage}
                  </p>
                )}

                <Button
                  size="lg"
                  className="w-full"
                  disabled={!canSubmit}
                  onClick={confirm}
                >
                  {isBusy ? <Spinner className="size-4" /> : null}
                  Give permission
                </Button>
              </CardContent>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
